import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@store/hooks';
import { setActiveTab } from '@store/slices/monitor.slice';
import { ShieldIcon } from '@utils/icons';
import { useSafetyVisible } from '../hooks';
import { SafetyAlertRow } from './SafetyAlertRow';
import { useSafetyAlertDialog } from './useSafetyAlertDialog';
import { useSafetySource } from './useRailSource';
import styles from './leftRail.module.css';

/** Most-recent alerts shown inline on the card before "view all". */
const MAX_ROWS = 3;

/**
 * Summary card showing the latest safety alerts in the LeftRail card stack.
 * Rows reuse the existing `SafetyAlertRow` (D-ORCA's standard safety-row
 * style). "View all" switches to the full safety view via `setActiveTab` —
 * the same mechanism the old Safety tab used.
 */
export function SafetyAlertsCard() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // Live, or the replayed day's up to the playhead.
  const { active, closed } = useSafetySource();
  const allowed = useSafetyVisible();
  const { openAlert, alertDialog } = useSafetyAlertDialog();

  // Active rows first (already critical-first / newest), then closed —
  // take the three most relevant.
  const recent = useMemo(
    () => [...active, ...closed].slice(0, MAX_ROWS),
    [active, closed],
  );

  // Hide the card entirely when the user lacks the safety permission.
  if (!allowed) return null;

  return (
    <section className={styles.section}>
      <div className={styles.cardHeader}>
        <span
          className={`${styles.cardHeaderIcon} ${styles.cardHeaderIconAlert}`}
        >
          <ShieldIcon size={16} />
        </span>
        <span className={styles.cardHeaderTitle}>
          {t('monitor.safetyAlerts.title')}{' '}
          <span className={styles.sectionCount}>
            ({active.length + closed.length})
          </span>
        </span>
        <button
          type="button"
          className={styles.viewAllButton}
          onClick={() => dispatch(setActiveTab('safety'))}
        >
          {t('monitor.safetyAlerts.viewAll')}
        </button>
      </div>
      <div className={styles.cardBody}>
        {recent.length > 0 ? (
          recent.map((row) => <SafetyAlertRow key={row.id} row={row} onOpen={openAlert} />)
        ) : (
          <div className={styles.sectionEmpty}>
            {t('monitor.safetyAlerts.empty')}
          </div>
        )}
      </div>
      {alertDialog}
    </section>
  );
}
